import type { ViewportsConfigurations } from '../ViewportsConfigurations/types';
import type { TStackProviders } from './ViewerContext';

import { useEffect } from 'react';

import usePrevious from 'react-use/esm/usePrevious';
import { useViewerMemoryConfig } from './useViewerMemoryConfig';
import { useViewerId } from 'hooks/useViewerId';
import { viewportIdToConfig } from './viewerUtils';
import { PixelDataSharedWorkerError } from 'modules/viewer/workers/pixelDataSharedWorkerTypes';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import { ImagingProviderLoadType } from '../ViewportDre/modules/imaging/BaseImagingProvider';
import { useCaseScopedState } from 'hooks/useCaseScopedState';

export type ViewerMemoryConfigMap = Map<string, ImagingProviderLoadType>;

export type ViewerMemoryConfig = {
  memoryLimit: number;
  usedMemory: Nullable<number>;
  memoryConfig: ViewerMemoryConfigMap;
};

export type ViewerMemoryManagerProps = {
  stackProviders: TStackProviders;
  viewportsConfigurations: Nullable<ViewportsConfigurations>;
  areViewportsReady: boolean;
};

// a high-level hook to manage the side effects of loading and unloading
// based on a memory configuration calculated without side effects
function useViewerMemoryManagement({
  stackProviders,
  viewportsConfigurations,
  areViewportsReady,
}: ViewerMemoryManagerProps): ViewerMemoryConfig {
  const { memoryLimit, usedMemory, memoryConfig } = useViewerMemoryConfig({
    stackProviders,
    viewportsConfigurations,
    areViewportsReady,
  });
  const previousMemoryConfig = usePrevious(memoryConfig);

  const viewerId = useViewerId();

  useTrackMemoryManagement({ memoryLimit, usedMemory, memoryConfig });

  // inject memory config into providers so they know what to do with new images
  useEffect(() => {
    memoryConfig.forEach((loadType, stackSmid) => {
      const provider = stackProviders.get(stackSmid);
      if (provider != null) provider.loadType = loadType;
    });
  }, [stackProviders, memoryConfig]);

  // throw imaging providers into error states that we don't have the space for
  // but are trying to render in a viewport
  useEffect(() => {
    memoryConfig.forEach((loadType, stackSmid) => {
      const provider = stackProviders.get(stackSmid);
      if (
        provider != null &&
        viewportsConfigurations != null &&
        loadType !== ImagingProviderLoadType.Full &&
        Object.entries(viewportsConfigurations).some(
          ([viewportId, vc]) =>
            vc?.stack?.smid === provider.linkSmid &&
            viewportIdToConfig(viewportId).windowId === viewerId
        )
      ) {
        provider.failLoading(PixelDataSharedWorkerError.NotEnoughMemory, true);
      }
    });
  }, [stackProviders, memoryConfig, viewportsConfigurations, viewerId]);

  // dynamically unload and load stacks' frames depending on what is on screen
  // and fits into the memory limits according to the memory configuration
  useEffect(() => {
    if (previousMemoryConfig == null) {
      return;
    }

    memoryConfig.forEach((loadType, stackSmid) => {
      const previousConfig = previousMemoryConfig.get(stackSmid);
      if (previousConfig == null) return;

      if (
        loadType === ImagingProviderLoadType.Full &&
        previousConfig !== ImagingProviderLoadType.Full
      ) {
        const provider = stackProviders.get(stackSmid);
        if (provider != null) {
          provider.reloadAllFramesIntoMemory();
        }
      }

      if (
        loadType === ImagingProviderLoadType.Initial &&
        previousConfig !== ImagingProviderLoadType.Initial
      ) {
        const provider = stackProviders.get(stackSmid);
        if (provider != null) {
          provider.loadFrameIntoMemory(provider.getActiveSlice('TWO_D_DRE'), {
            unloadOthers: true,
          });
        }
      }

      if (
        loadType === ImagingProviderLoadType.None &&
        previousConfig !== ImagingProviderLoadType.None
      ) {
        const provider = stackProviders.get(stackSmid);
        if (provider != null) {
          provider.unloadAllFrames();
        }
      }
    });
  }, [stackProviders, memoryConfig, previousMemoryConfig]);

  return { memoryLimit, usedMemory, memoryConfig };
}

function ViewerMemoryManager({
  stackProviders,
  viewportsConfigurations,
  areViewportsReady,
}: ViewerMemoryManagerProps): React.ReactElement | null {
  useViewerMemoryManagement({
    stackProviders,
    viewportsConfigurations,
    areViewportsReady,
  });
  return null;
}

function useTrackMemoryManagement({ memoryLimit, usedMemory, memoryConfig }: ViewerMemoryConfig) {
  const currentCaseId = useCurrentCaseId();

  const [maxMemoryUsedForThisCase, setMaxMemoryUsedForThisCase] = useCaseScopedState<number>(0);

  useEffect(() => {
    if (currentCaseId != null && usedMemory != null && maxMemoryUsedForThisCase < usedMemory) {
      setMaxMemoryUsedForThisCase(usedMemory);
    }
  }, [currentCaseId, usedMemory, maxMemoryUsedForThisCase, setMaxMemoryUsedForThisCase]);

  useEffect(() => {
    // Wait for state-update effect to complete if necessary.
    // This helps avoid double-calling addViewContext when a new larger memory config arrives.
    if (currentCaseId != null && usedMemory != null && maxMemoryUsedForThisCase >= usedMemory) {
      const pixelMemoryAnalytics = {
        used_memory: usedMemory,
        memory_limit: memoryLimit,
        used_ratio: usedMemory / memoryLimit,
        max_used: maxMemoryUsedForThisCase,
        // Memory limits are generally stable, except when flags are changed,
        // so we can keep it light and assume for analytics the memory limit
        // is the same as when it peaked.
        max_used_ratio: maxMemoryUsedForThisCase / memoryLimit,
        memory_configuration: Object.fromEntries(memoryConfig.entries()),
      };

      analytics?.addViewContext(globalContext.viewer.pixelMemory, pixelMemoryAnalytics);
    }
  }, [currentCaseId, memoryLimit, usedMemory, maxMemoryUsedForThisCase, memoryConfig]);
}

export { ViewerMemoryManager, useViewerMemoryManagement as __test_only__useViewerMemoryManagement };
